import { useEffect, useState } from "react";

/**
 * Family photo showcase for the "About Me" section.
 * - Main photo cross-fades every 5s
 * - Active image slowly zooms (Ken Burns)
 * - Small "floating" thumbnails drift around the frame
 */
const PHOTOS = [
  { src: "/assets/products/candle_12oz.jpg", caption: "Sanctuary Grand Candle · hand-poured" },
  { src: "/assets/oil_duo.png", caption: "Hair Growth Oil · 2 oz & 4 oz" },
  { src: "/assets/combos/combo_3.png", caption: "The Grand Gathering combo" },
  { src: "/assets/products/tumbler_yellow_pink.png", caption: "Sunset Two-Tone Tumbler" },
  { src: "/assets/tshirt_front_back.png", caption: "Signature Rooted-in-Faith tee" },
  { src: "/assets/products/planter_small.jpg", caption: "Small Floral Planter" },
  { src: "/assets/products/candle_2oz.jpg", caption: "Petite candle · gift-ready" },
  { src: "/assets/combos/combo_6.png", caption: "The Full Studio combo" },
];

export default function AboutMePhotos({ className = "" }) {
  const [active, setActive] = useState(0);
  const [zoomKey, setZoomKey] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setActive((i) => (i + 1) % PHOTOS.length);
      setZoomKey((k) => k + 1);
    }, 5000);
    return () => clearInterval(id);
  }, []);

  const thumbs = [
    (active + 1) % PHOTOS.length,
    (active + 3) % PHOTOS.length,
    (active + 5) % PHOTOS.length,
  ];

  return (
    <div
      data-testid="about-me-photos"
      className={`relative w-full aspect-[3/4] max-w-[520px] mx-auto ${className}`}
    >
      {/* Peach ring frame */}
      <div className="absolute -inset-3 rounded-[2.5rem] border-[6px] border-[#C68465]/70 rotate-[-1.5deg] z-0" />

      {/* Cross-fading main photo */}
      <div className="relative w-full h-full rounded-[2rem] overflow-hidden shadow-2xl z-10 bg-brand-moss/10">
        {PHOTOS.map((p, i) => (
          <img
            key={p.src}
            src={p.src}
            alt={p.caption}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[1600ms] ease-in-out ${
              i === active ? "opacity-100" : "opacity-0"
            }`}
            style={
              i === active
                ? {
                    animation: "kenburns 6.5s ease-in-out forwards",
                    animationName: "kenburns",
                  }
                : undefined
            }
            // key trick above forces the CSS animation to restart per active photo
            data-active={i === active ? "true" : "false"}
            data-testid={i === active ? "about-me-active-photo" : undefined}
          />
        ))}
        {/* zoom re-trigger overlay (invisible, re-mounted per zoomKey) */}
        <span key={zoomKey} className="hidden" />

        {/* soft gradient at bottom for caption legibility */}
        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black/55 via-black/10 to-transparent z-10" />

        {/* caption */}
        <div className="absolute bottom-4 left-4 right-4 z-20">
          <p className="font-script text-2xl text-brand-cream leading-tight">
            {PHOTOS[active].caption}
          </p>
        </div>

        {/* progress dots */}
        <div className="absolute top-4 right-4 z-20 flex gap-1.5">
          {PHOTOS.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all duration-500 ${
                i === active ? "w-6 bg-brand-gold" : "w-1.5 bg-brand-cream/50"
              }`}
            />
          ))}
        </div>
      </div>

      {/* Floating thumbnails around the frame */}
      <div
        className="absolute -top-8 -left-10 w-24 h-24 rounded-2xl overflow-hidden shadow-xl rotate-[-8deg] z-20 hidden md:block"
        style={{ animation: "floatY 6s ease-in-out infinite" }}
      >
        <img src={PHOTOS[thumbs[0]].src} alt="" aria-hidden="true" className="w-full h-full object-cover" />
      </div>
      <div
        className="absolute top-1/3 -right-14 w-28 h-28 rounded-2xl overflow-hidden shadow-xl rotate-[6deg] z-20 hidden md:block"
        style={{ animation: "floatY 7s ease-in-out infinite", animationDelay: "1.2s" }}
      >
        <img src={PHOTOS[thumbs[1]].src} alt="" aria-hidden="true" className="w-full h-full object-cover" />
      </div>
      <div
        className="absolute -bottom-8 -left-12 w-24 h-24 rounded-2xl overflow-hidden shadow-xl rotate-[7deg] z-20 hidden md:block"
        style={{ animation: "floatY 8s ease-in-out infinite", animationDelay: "0.5s" }}
      >
        <img src={PHOTOS[thumbs[2]].src} alt="" aria-hidden="true" className="w-full h-full object-cover" />
      </div>

      <style>{`
        @keyframes kenburns {
          0%   { transform: scale(1) translate(0, 0); }
          50%  { transform: scale(1.08) translate(-1.5%, -2%); }
          100% { transform: scale(1.12) translate(1.5%, -1%); }
        }
        @keyframes floatY {
          0%, 100% { transform: translateY(0) rotate(var(--r, 0deg)); }
          50%      { transform: translateY(-14px) rotate(var(--r, 0deg)); }
        }
      `}</style>
    </div>
  );
}
